<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />

    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="w-full mx-auto space-y-5 max-w-7xl">
          <!-- Loading skeleton -->
          <div
            v-if="isLoading"
            class="grid grid-cols-1 gap-4 lg:grid-cols-3 animate-pulse"
          >
            <div class="space-y-4 lg:col-span-2">
              <div class="h-56 rounded-2xl bg-slate-200/70 dark:bg-slate-800"></div>
              <div class="h-96 rounded-2xl bg-slate-200/70 dark:bg-slate-800"></div>
            </div>
            <div class="h-64 rounded-2xl bg-slate-200/70 dark:bg-slate-800"></div>
          </div>

          <!-- Not found -->
          <div
            v-else-if="notFound"
            class="flex flex-col items-center justify-center py-20 text-center border shadow-sm rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 backdrop-blur"
          >
            <i class="mb-3 text-4xl mdi mdi-ticket-outline text-slate-300"></i>
            <p class="text-sm text-slate-500">{{ t("ticket_not_found") }}</p>
            <RouterLink
              to="/tickets/my"
              class="mt-4 px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90"
            >
              {{ t("ticket_back_list") }}
            </RouterLink>
          </div>

          <div
            v-else-if="ticket"
            class="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-start"
          >
            <!-- MAIN COLUMN -->
            <div class="space-y-4 lg:col-span-2">
              <!-- Ticket header -->
              <section
                class="relative p-4 overflow-hidden border shadow-sm md:p-5 rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200/80 dark:border-slate-700/80 backdrop-blur"
              >
                <div
                  class="pointer-events-none absolute -top-12 -end-10 w-40 h-40 rounded-[60px] bg-secondary/20 blur-2xl"
                ></div>

                <div class="relative z-10 flex flex-col gap-3">
                  <div class="flex flex-wrap items-start justify-between gap-3">
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <h1
                          class="text-base font-bold text-slate-900 md:text-lg dark:text-white"
                        >
                          {{ t("ticket_detail_title") }}
                        </h1>
                        <TicketNumberBadge
                          :ticket-number="ticket.ticket_number"
                          :copy-title="t('ticket_copy_number')"
                        />
                      </div>
                      <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {{ t("ticket_detail_subtitle") }}
                      </p>
                    </div>

                    <div
                      class="flex flex-wrap items-center gap-2 px-2 py-1 border rounded-xl bg-white/90 dark:bg-slate-900/90 border-slate-200 dark:border-slate-700 backdrop-blur"
                    >
                      <div
                        class="flex items-center gap-2 text-xs text-slate-500 me-1 pe-2 border-e border-slate-200 dark:border-slate-700"
                      >
                        <i class="mdi mdi-flash text-[14px] text-secondary"></i>
                        {{ t("ticket_table_actions") }}
                      </div>

                      <RouterLink
                        to="/tickets/my"
                        class="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800"
                      >
                        {{ t("ticket_back_list") }}
                      </RouterLink>

                      <button
                        v-if="ticket.can?.reopen"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-secondary/30 text-secondary bg-secondary/10 hover:bg-secondary/15 disabled:opacity-60"
                        :disabled="actionLoading"
                        :title="t('ticket_reopen_hint')"
                        @click="openActionModal('reopen')"
                      >
                        <i class="mdi mdi-replay text-[14px]"></i>
                        {{ t("ticket_reopen") }}
                      </button>

                      <button
                        v-if="ticket.can?.escalate"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-secondary hover:bg-secondary/90 disabled:opacity-60"
                        :disabled="actionLoading"
                        :title="t('ticket_escalate_hint')"
                        @click="openActionModal('escalate')"
                      >
                        <i class="mdi mdi-arrow-up-bold-circle-outline text-[14px]"></i>
                        {{ t("ticket_escalate") }}
                      </button>

                      <!-- Close ticket (resolved → closed) — uncomment to re-enable
                      <button
                        v-if="canClose"
                        type="button"
                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-60"
                        :disabled="actionLoading"
                        :title="t('ticket_close_hint')"
                        @click="openActionModal('close')"
                      >
                        <i class="mdi mdi-check-circle-outline text-[14px]"></i>
                        {{ t("ticket_close") }}
                      </button>
                      -->
                    </div>
                  </div>

                  <h2
                    class="text-sm font-bold leading-relaxed break-words md:text-base text-slate-900 dark:text-white"
                  >
                    {{ ticket.subject }}
                  </h2>

                  <!-- Quick chips -->
                  <div class="flex flex-wrap items-center gap-2 text-[10px]">
                    <TicketStatusBadge
                      :status="ticket.status"
                      :unassigned="isUnassigned"
                      :lang="lang"
                      :t="t"
                    />
                    <!-- Priority badge — uncomment to re-enable
                    <TicketPriorityBadge
                      :priority="ticket.priority"
                      :lang="lang"
                    />
                    -->
                    <span
                      v-if="ticket.is_overdue"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-50 text-rose-600 dark:bg-rose-900/20 dark:text-rose-300"
                    >
                      <i class="mdi mdi-clock-alert-outline text-[14px]"></i>
                      {{ t("ticket_overdue") }}
                    </span>
                    <!-- Party (department) chip — uncomment to re-enable
                    <span
                      v-if="departmentName"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-secondary/15 text-secondary"
                    >
                      <i class="mdi mdi-domain text-[14px]"></i>
                      {{ departmentName }}
                    </span>
                    -->
                    <span
                      v-if="categoryName"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/10 text-slate-700 dark:text-slate-200"
                    >
                      <i class="mdi mdi-shape-outline text-[14px]"></i>
                      {{ categoryName }}
                    </span>
                    <span
                      v-if="ticket.created_at"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                    >
                      <i class="mdi mdi-calendar-month-outline text-[14px]"></i>
                      {{ formatTicketDateTime(ticket.created_at, lang) }}
                    </span>
                  </div>

                  <!-- Requester / party -->
                  <div class="grid grid-cols-1 gap-3 mt-1 md:grid-cols-2">
                    <div class="flex items-start gap-2">
                      <i
                        class="mdi mdi-account-tie-outline text-[16px] text-slate-500"
                      ></i>
                      <div class="min-w-0">
                        <div class="text-xs text-slate-500">
                          {{ t("ticket_requester") }}
                        </div>
                        <div
                          class="text-sm font-semibold truncate text-slate-800 dark:text-slate-100"
                        >
                          {{ creatorDisplayName(ticket, lang) }}
                        </div>
                      </div>
                    </div>

                    <!-- Party (department) — uncomment to re-enable
                    <div class="flex items-start gap-2">
                      <i class="mdi mdi-domain text-[16px] text-slate-500"></i>
                      <div class="min-w-0">
                        <div class="text-xs text-slate-500">
                          {{ t("ticket_table_party") }}
                        </div>
                        <div
                          class="text-sm font-semibold truncate text-slate-800 dark:text-slate-100"
                        >
                          {{ departmentName || "—" }}
                        </div>
                      </div>
                    </div>
                    -->
                  </div>

                  <!-- Description -->
                  <div class="flex items-start gap-2 mt-1">
                    <i class="mdi mdi-text text-[16px] text-slate-500"></i>
                    <div class="flex-1 min-w-0">
                      <div class="text-xs text-slate-500">
                        {{ t("ticket_description") }}
                      </div>
                      <div
                        class="text-sm leading-relaxed break-words whitespace-pre-wrap text-slate-700 dark:text-slate-200"
                      >
                        {{ ticket.description || "—" }}
                      </div>
                    </div>
                  </div>

                  <!-- Attachments -->
                  <div
                    class="pt-3 mt-1 border-t border-slate-200/70 dark:border-slate-700/70"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <h3
                        class="text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                      >
                        {{ t("ticket_attachments") }}
                      </h3>
                      <span
                        class="inline-flex items-center justify-center text-xs font-semibold rounded-full w-7 h-7 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                      >
                        {{ ticketAttachments.length }}
                      </span>
                    </div>

                    <TicketAttachmentGrid
                      v-if="ticketAttachments.length"
                      :attachments="ticketAttachments"
                      :lang="lang"
                      :title="t('ticket_attachments')"
                      compact
                    />
                    <p v-else class="text-xs text-slate-500">
                      {{ t("ticket_no_attachments") }}
                    </p>
                  </div>
                </div>
              </section>

              <!-- Discussion / activity -->
              <section
                class="flex flex-col overflow-hidden border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur min-h-[420px]"
              >
                <div
                  class="flex items-center gap-1 px-3 pt-2 border-b md:px-4 border-slate-200/70 dark:border-slate-700/70 shrink-0"
                >
                  <button
                    type="button"
                    class="relative px-4 py-2.5 text-xs font-semibold rounded-t-xl transition-colors"
                    :class="
                      activeTab === 'discussion'
                        ? 'text-primary bg-primary/5'
                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    "
                    @click="activeTab = 'discussion'"
                  >
                    <i
                      class="mdi mdi-comment-text-multiple-outline"
                      :class="lang === 'ar' ? 'ml-1' : 'mr-1'"
                    ></i>
                    {{ t("ticket_comments") }}
                    <span
                      v-if="comments.length"
                      class="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[9px] font-bold rounded-full bg-secondary/15 text-secondary"
                      :class="lang === 'ar' ? 'mr-1' : 'ml-1'"
                    >
                      {{ comments.length }}
                    </span>
                    <span
                      v-if="activeTab === 'discussion'"
                      class="absolute bottom-0 left-3 right-3 h-0.5 bg-primary rounded-full"
                    ></span>
                  </button>

                  <button
                    type="button"
                    class="relative px-4 py-2.5 text-xs font-semibold rounded-t-xl transition-colors"
                    :class="
                      activeTab === 'activity'
                        ? 'text-primary bg-primary/5'
                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                    "
                    @click="activeTab = 'activity'"
                  >
                    <i
                      class="mdi mdi-timeline-clock-outline"
                      :class="lang === 'ar' ? 'ml-1' : 'mr-1'"
                    ></i>
                    {{ t("ticket_tab_activity") }}
                    <span
                      v-if="activitiesTotal"
                      class="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[9px] font-bold rounded-full bg-primary/10 text-primary"
                      :class="lang === 'ar' ? 'mr-1' : 'ml-1'"
                    >
                      {{ activitiesTotal }}
                    </span>
                    <span
                      v-if="activeTab === 'activity'"
                      class="absolute bottom-0 left-3 right-3 h-0.5 bg-primary rounded-full"
                    ></span>
                  </button>
                </div>

                <div class="flex flex-col flex-1 min-h-0 p-4 md:p-5">
                  <div
                    v-if="activeTab === 'discussion'"
                    key="tab-discussion"
                    class="flex flex-col flex-1 min-h-0 gap-4"
                  >
                    <CommentThread
                      :items="comments"
                      :lang="lang"
                      :t="t"
                      :title="t('ticket_comments')"
                      :empty-label="t('ticket_comments_empty')"
                      :empty-hint="t('ticket_comments_empty_hint')"
                      :legend-creator="t('ticket_comment_creator')"
                      :legend-handler="t('ticket_comment_handler')"
                      :ticket-creator="ticketCreator"
                      :ticket-handler="ticketHandler"
                      :current-user-id="currentUserId"
                    />

                    <div v-if="ticket.can?.comment" class="shrink-0">
                      <CommentForm
                        :loading="isCommenting"
                        :lang="lang"
                        :placeholder="t('ticket_comment_placeholder')"
                        :submit-label="t('ticket_comment_submit')"
                        :attachments-hint="t('ticket_attachments_hint')"
                        :dropzone-title="t('ticket_dropzone_title')"
                        @submit="onComment"
                        @error="showToast($event, 'error')"
                      />
                    </div>

                    <div
                      v-else
                      class="py-4 text-center text-[11px] text-slate-400 border border-dashed rounded-xl border-slate-200 dark:border-slate-700 shrink-0"
                    >
                      {{ t("ticket_comments_closed") }}
                    </div>
                  </div>

                  <div
                    v-else-if="activeTab === 'activity'"
                    key="tab-activity"
                    class="flex flex-col flex-1 min-h-[360px]"
                  >
                    <ActivityTimeline
                      :items="activities"
                      :loading="activitiesLoading"
                      :has-more="activitiesHasMore"
                      :total-count="activitiesTotal"
                      :lang="lang"
                      :t="t"
                      scroll-class="max-h-[min(560px,65vh)]"
                      @load-more="loadActivities(activitiesPage + 1, true)"
                    />
                  </div>
                </div>
              </section>
            </div>

            <!-- SIDE COLUMN -->
            <aside class="space-y-4 lg:col-span-1 lg:sticky lg:top-4">
              <section
                class="relative p-5 overflow-hidden border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
              >
                <div
                  class="absolute rounded-full pointer-events-none -bottom-10 -end-10 w-28 h-28 bg-primary/10"
                ></div>

                <h3
                  class="mb-3 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                >
                  {{ t("ticket_summary") }}
                </h3>

                <dl class="relative z-10 space-y-2.5 text-xs">
                  <div class="flex items-center justify-between gap-3">
                    <dt class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-flag-outline text-[16px]"></i>
                      <span>{{ t("ticket_table_status") }}</span>
                    </dt>
                    <dd>
                      <TicketStatusBadge
                        :status="ticket.status"
                        :unassigned="isUnassigned"
                        :lang="lang"
                        :t="t"
                      />
                    </dd>
                  </div>

                  <!-- Priority — uncomment to re-enable
                  <div class="flex items-center justify-between gap-3">
                    <dt class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-priority-high text-[16px]"></i>
                      <span>{{ t("ticket_priority") }}</span>
                    </dt>
                    <dd>
                      <TicketPriorityBadge
                        :priority="ticket.priority"
                        :lang="lang"
                      />
                    </dd>
                  </div>
                  -->

                  <!-- Assignee — uncomment to re-enable
                  <div class="flex items-center justify-between gap-3">
                    <dt class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-account-hard-hat text-[16px]"></i>
                      <span>{{ t("ticket_assigned_to") }}</span>
                    </dt>
                    <dd
                      class="font-semibold truncate text-slate-700 dark:text-slate-200"
                    >
                      {{ assigneeName || t("ticket_unassigned") }}
                    </dd>
                  </div>
                  -->

                  <!-- Party (department) — uncomment to re-enable
                  <div class="flex items-center justify-between gap-3">
                    <dt class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-domain text-[16px]"></i>
                      <span>{{ t("ticket_table_party") }}</span>
                    </dt>
                    <dd
                      class="font-semibold truncate text-slate-700 dark:text-slate-200"
                    >
                      {{ departmentName || "—" }}
                    </dd>
                  </div>
                  -->

                  <div
                    v-if="categoryName"
                    class="flex items-center justify-between gap-3"
                  >
                    <dt class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-shape-outline text-[16px]"></i>
                      <span>{{ t("ticket_category") }}</span>
                    </dt>
                    <dd
                      class="font-semibold truncate text-slate-700 dark:text-slate-200"
                    >
                      {{ categoryName }}
                    </dd>
                  </div>

                  <div
                    class="flex items-center justify-between gap-3 pt-2.5 border-t border-slate-200 dark:border-slate-700"
                  >
                    <dt class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-calendar-plus text-[16px]"></i>
                      <span>{{ t("ticket_created_at") }}</span>
                    </dt>
                    <dd
                      class="font-semibold tabular-nums text-slate-700 dark:text-slate-200"
                    >
                      {{ formatTicketDateTime(ticket.created_at, lang) }}
                    </dd>
                  </div>

                  <div class="flex items-center justify-between gap-3">
                    <dt class="flex items-center gap-2 text-slate-500">
                      <i class="mdi mdi-update text-[16px]"></i>
                      <span>{{ t("ticket_table_updated") }}</span>
                    </dt>
                    <dd
                      class="font-semibold tabular-nums text-slate-700 dark:text-slate-200"
                    >
                      {{ formatTicketDateTime(ticket.updated_at, lang) }}
                    </dd>
                  </div>
                </dl>
              </section>

              <!-- Action hints -->
              <section
                v-if="ticket.can?.reopen || ticket.can?.escalate"
                class="p-5 space-y-3 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
              >
                <h3
                  class="text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                >
                  {{ t("ticket_table_actions") }}
                </h3>

                <button
                  v-if="ticket.can?.reopen"
                  type="button"
                  class="flex items-start w-full gap-3 p-3 text-start border rounded-xl border-secondary/25 bg-secondary/5 hover:bg-secondary/10 transition-colors disabled:opacity-60"
                  :disabled="actionLoading"
                  @click="openActionModal('reopen')"
                >
                  <span
                    class="flex items-center justify-center rounded-lg w-9 h-9 shrink-0 bg-secondary/15 text-secondary"
                  >
                    <i class="mdi mdi-replay text-[18px]"></i>
                  </span>
                  <span class="min-w-0">
                    <span class="block text-xs font-semibold text-secondary">
                      {{ t("ticket_reopen") }}
                    </span>
                    <span
                      class="block mt-0.5 text-[10px] leading-snug text-slate-500"
                    >
                      {{ t("ticket_reopen_hint") }}
                    </span>
                  </span>
                </button>

                <button
                  v-if="ticket.can?.escalate"
                  type="button"
                  class="flex items-start w-full gap-3 p-3 text-start border rounded-xl border-amber-200 bg-amber-50/70 hover:bg-amber-100/70 dark:border-amber-900/40 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-60"
                  :disabled="actionLoading"
                  @click="openActionModal('escalate')"
                >
                  <span
                    class="flex items-center justify-center rounded-lg w-9 h-9 shrink-0 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                  >
                    <i class="mdi mdi-arrow-up-bold-circle-outline text-[18px]"></i>
                  </span>
                  <span class="min-w-0">
                    <span
                      class="block text-xs font-semibold text-amber-800 dark:text-amber-200"
                    >
                      {{ t("ticket_escalate") }}
                    </span>
                    <span
                      class="block mt-0.5 text-[10px] leading-snug text-slate-500"
                    >
                      {{ t("ticket_escalate_hint") }}
                    </span>
                  </span>
                </button>

                <!-- Close ticket (resolved → closed) — uncomment to re-enable
                <button
                  v-if="canClose"
                  type="button"
                  class="flex items-start w-full gap-3 p-3 text-start border rounded-xl border-primary/25 bg-primary/5 hover:bg-primary/10 transition-colors disabled:opacity-60"
                  :disabled="actionLoading"
                  @click="openActionModal('close')"
                >
                  <span
                    class="flex items-center justify-center rounded-lg w-9 h-9 shrink-0 bg-primary/10 text-primary"
                  >
                    <i class="mdi mdi-check-circle-outline text-[18px]"></i>
                  </span>
                  <span class="min-w-0">
                    <span class="block text-xs font-semibold text-primary">
                      {{ t("ticket_close") }}
                    </span>
                    <span
                      class="block mt-0.5 text-[10px] leading-snug text-slate-500"
                    >
                      {{ t("ticket_close_hint") }}
                    </span>
                  </span>
                </button>
                -->
              </section>
            </aside>
          </div>
        </div>
      </main>
    </div>

    <!-- Action modal -->
    <Teleport to="body">
      <div
        v-if="actionModal"
        class="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
        @click.self="closeActionModal"
      >
        <div
          class="w-full max-w-md p-5 bg-white border shadow-2xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl animate-[fadeIn_0.2s_ease]"
          :class="lang === 'ar' ? 'text-right' : 'text-left'"
        >
          <div v-if="activeAction" class="flex items-start gap-3 mb-4">
            <span
              class="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl"
              :class="activeAction.tint"
            >
              <i class="mdi text-xl" :class="activeAction.icon"></i>
            </span>
            <div>
              <h3 class="text-sm font-bold text-slate-900 dark:text-white">
                {{ t(activeAction.title) }}
              </h3>
              <p class="mt-1 text-[11px] text-slate-500">
                {{ t(activeAction.hint) }}
              </p>
            </div>
          </div>

          <template v-if="activeAction?.note">
            <label
              class="block mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ t("ticket_note_optional") }}
            </label>
            <textarea
              v-model="actionNote"
              rows="3"
              class="w-full px-3 py-2 text-xs border rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            ></textarea>
          </template>

          <p
            v-else-if="activeAction?.confirm"
            class="px-3 py-2.5 text-xs leading-relaxed border rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300"
          >
            {{ t(activeAction.confirm) }}
          </p>

          <div class="flex justify-end gap-2 mt-4">
            <button
              type="button"
              class="px-4 py-2 text-xs font-medium border rounded-xl border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              @click="closeActionModal"
            >
              {{ lang === "ar" ? "إلغاء" : "Cancel" }}
            </button>
            <button
              type="button"
              class="px-4 py-2 text-xs font-semibold text-white rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-60"
              :disabled="actionLoading"
              @click="confirmAction"
            >
              <i
                v-if="actionLoading"
                class="mdi mdi-loading mdi-spin"
                :class="lang === 'ar' ? 'ml-1' : 'mr-1'"
              ></i>
              {{ lang === "ar" ? "تأكيد" : "Confirm" }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Toast
      :show="toast.show"
      :type="toast.type"
      :message="toast.message"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import TicketNumberBadge from "@/components/tickets/TicketNumberBadge.vue";
import TicketStatusBadge from "@/components/tickets/TicketStatusBadge.vue";
// import TicketPriorityBadge from "@/components/tickets/TicketPriorityBadge.vue";
import ActivityTimeline from "@/components/tickets/ActivityTimeline.vue";
import CommentThread from "@/components/tickets/CommentThread.vue";
import CommentForm from "@/components/tickets/CommentForm.vue";
import TicketAttachmentGrid from "@/components/tickets/TicketAttachmentGrid.vue";
import { ticketsApi } from "@/api/tickets";
import { useTicketMetaStore } from "@/stores/ticketMeta";
import { usePageI18n } from "@/composables/usePageI18n";
import { useAuthShell } from "@/composables/useAuthShell";
import {
  pickTranslatedName,
  formatTicketDateTime,
  getStatusCode,
  creatorDisplayName,
} from "@/utils/ticket";

const { lang, t } = usePageI18n();
const {
  isSidebarCollapsed,
  isSidebarOpenMobile,
  sidebarSearch,
  toggleSidebar,
  toast,
  showToast,
} = useAuthShell();

const route = useRoute();
const ticketMeta = useTicketMetaStore();

const ticket = ref(null);
const currentUser = ref(null);
const isLoading = ref(true);
const notFound = ref(false);
const activeTab = ref("discussion");

const comments = ref([]);
const activities = ref([]);
const activitiesLoading = ref(false);
const activitiesPage = ref(1);
const activitiesHasMore = ref(false);
const activitiesTotal = ref(0);

const isCommenting = ref(false);
const actionModal = ref(null);
const actionNote = ref("");
const actionLoading = ref(false);

const ticketNumber = computed(() => route.params.ticketNumber);

const isUnassigned = computed(() => {
  if (!ticket.value) return false;
  return !ticket.value.assigned_to && getStatusCode(ticket.value.status) === "open";
});

/*
 * Close ticket (resolved status_id 3 → closed) — uncomment to re-enable.
 *
 * const RESOLVED_STATUS_ID = 3;
 *
 * const canClose = computed(() => {
 *   const value = ticket.value;
 *   if (!value) return false;
 *   return (
 *     getStatusCode(value.status) === "resolved" ||
 *     value.status?.id === RESOLVED_STATUS_ID ||
 *     value.status_id === RESOLVED_STATUS_ID
 *   );
 * });
 */

/** Per-action modal copy and request, keyed by the value of `actionModal`. */
const ACTIONS = {
  reopen: {
    icon: "mdi-replay",
    tint: "bg-secondary/10 text-secondary",
    title: "ticket_reopen",
    hint: "ticket_reopen_hint",
    success: "ticket_reopen_success",
    note: true,
    request: (note) => ticketsApi.reopen(ticketNumber.value, note),
  },
  escalate: {
    icon: "mdi-arrow-up-bold-circle-outline",
    tint: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    title: "ticket_escalate",
    hint: "ticket_escalate_hint",
    success: "ticket_escalate_success",
    note: true,
    request: (note) => ticketsApi.escalate(ticketNumber.value, note),
  },
  /*
  close: {
    icon: "mdi-check-circle-outline",
    tint: "bg-primary/10 text-primary",
    title: "ticket_close",
    hint: "ticket_close_hint",
    success: "ticket_close_success",
    confirm: "ticket_close_confirm",
    request: () => ticketsApi.close(ticketNumber.value),
  },
  */
};

const activeAction = computed(() => ACTIONS[actionModal.value] || null);

const ticketAttachments = computed(() => {
  const list = ticket.value?.attachments;
  return Array.isArray(list) ? list : [];
});

/*
const departmentName = computed(() =>
  pickTranslatedName(ticket.value?.department, lang.value),
);
*/

const categoryName = computed(() =>
  pickTranslatedName(ticket.value?.category, lang.value),
);

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function loadUserFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) return (currentUser.value = null);
  try {
    currentUser.value = JSON.parse(raw);
  } catch {
    currentUser.value = null;
  }
}

const currentUserId = computed(
  () => currentUser.value?.id ?? currentUser.value?.user_id ?? null,
);

/** Identity of whoever opened the ticket, used to label comment authors. */
const ticketCreator = computed(() => {
  const source =
    ticket.value?.creator || ticket.value?.created_by || ticket.value?.user;
  const id = source?.id ?? ticket.value?.created_by_id ?? null;
  const name = source?.name || ticket.value?.creator_name || "";
  if (id == null && !name) return null;
  return { id, name };
});

const assigneeName = computed(() => {
  const assignee = ticket.value?.assigned_to;
  if (!assignee) return "";
  if (typeof assignee === "string") return assignee;
  return assignee.name || assignee.full_name || "";
});

/** Identity of the assigned agent, used to label handler comments. */
const ticketHandler = computed(() => {
  const assignee = ticket.value?.assigned_to;
  const id = typeof assignee === "object" ? (assignee?.id ?? null) : null;
  const name = assigneeName.value;
  if (id == null && !name) return null;
  return { id, name };
});

async function loadTicket() {
  isLoading.value = true;
  notFound.value = false;
  try {
    ticket.value = await ticketsApi.show(ticketNumber.value);
    comments.value = ticket.value?.comments || [];
    activitiesPage.value = 1;
    await loadActivities(1, false);
  } catch (e) {
    console.error(e);
    if (e?.response?.status === 404) {
      notFound.value = true;
    } else {
      showToast(t("ticket_load_failed"), "error");
    }
    ticket.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function loadActivities(page = 1, append = false) {
  activitiesLoading.value = true;
  try {
    const res = await ticketsApi.activities(ticketNumber.value, page, 20);
    const rows = res.data || [];
    activities.value = append ? [...activities.value, ...rows] : rows;
    activitiesPage.value = page;
    activitiesTotal.value = res.meta?.total ?? activities.value.length;
    activitiesHasMore.value =
      (res.meta?.current_page ?? page) < (res.meta?.last_page ?? page);
  } catch (e) {
    console.error(e);
  } finally {
    activitiesLoading.value = false;
  }
}

/** Adds a comment to the oldest-first thread, ignoring one already listed. */
function appendComment(list, comment) {
  if (!comment) return list;
  if (comment.id != null && list.some((item) => item.id === comment.id)) {
    return list;
  }
  return [...list, comment];
}

async function onComment({ comment, attachments }) {
  isCommenting.value = true;
  try {
    const res = await ticketsApi.addComment(ticketNumber.value, {
      comment,
      attachments,
    });

    // The endpoint returns the ticket resource, which carries `latest_comment`
    // rather than the full `comments` array.
    const payload = res.data?.data ?? res.data ?? null;
    const fresh =
      payload?.ticket_number || Array.isArray(payload?.comments)
        ? payload
        : await ticketsApi.show(ticketNumber.value);

    ticket.value = fresh ?? ticket.value;
    comments.value = Array.isArray(fresh?.comments)
      ? fresh.comments
      : appendComment(comments.value, fresh?.latest_comment);
    showToast(res.message || t("ticket_comment_success"));
    activeTab.value = "discussion";
    await loadActivities(1, false);
  } catch (e) {
    console.error(e);
    showToast(
      e?.response?.data?.message || t("ticket_comment_failed"),
      "error",
    );
  } finally {
    isCommenting.value = false;
  }
}

function openActionModal(type) {
  actionModal.value = type;
  actionNote.value = "";
}

function closeActionModal() {
  actionModal.value = null;
  actionNote.value = "";
}

async function confirmAction() {
  const action = activeAction.value;
  if (!action || actionLoading.value) return;
  actionLoading.value = true;
  try {
    const res = await action.request(actionNote.value.trim());

    ticket.value = res.data?.data ?? res.data ?? ticket.value;
    comments.value = ticket.value?.comments || comments.value;
    showToast(res.message || t(action.success));
    closeActionModal();
    activeTab.value = "activity";
    await loadActivities(1, false);
  } catch (e) {
    console.error(e);
    showToast(e?.response?.data?.message || t("ticket_action_failed"), "error");
  } finally {
    actionLoading.value = false;
  }
}

watch(
  () => route.params.ticketNumber,
  (val) => {
    if (val) loadTicket();
  },
);

onMounted(async () => {
  loadUserFromCookie();
  await ticketMeta.fetchMetadataOnce();
  loadTicket();
});
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.97) translateY(4px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
